import React, { useState, useEffect } from "react";
import "./Reviews.css";
import { IMAGES, PUBLIC_ASSETS } from "../../utils/imageAssets";

// 윈도우 크기 감지 훅
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};

const Reviews: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStory, setCurrentStory] = useState(1);
  const { width, height } = useWindowSize();

  const isMobile = width <= 768;
  const isSmallMobile = width <= 480;

  const handleStoryClick = (storyNumber: number) => {
    setCurrentStory(storyNumber);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handlePrevStory = () => {
    setCurrentStory((current) => (current === 1 ? 3 : current - 1));
  };

  const handleNextStory = () => {
    setCurrentStory((current) => (current === 3 ? 1 : current + 1));
  };

  const getStoryImage = (storyNumber: number) => {
    switch (storyNumber) {
      case 1:
        return PUBLIC_ASSETS.STORIES.STORY_1;
      case 2:
        return PUBLIC_ASSETS.STORIES.STORY_2;
      case 3:
        return PUBLIC_ASSETS.STORIES.STORY_3;
      default:
        return PUBLIC_ASSETS.STORIES.STORY_1;
    }
  };

  const reviews = [
    {
      id: 1,
      name: "이정희 님",
      role: "복지관 동호회 회장",
      picture: IMAGES.REVIEWS.PROFILE_1,
      text: `"저는 그냥 평범한 주부였어요. 남편 뒷바라지하고 아이들 키우고... 특별할 게 뭐 있겠어요?"`,
      highlight: `"할머니, 진짜 슈퍼맨이었네요."`,
    },
    {
      id: 2,
      name: "박미선 님",
      role: "가족 스토리북 주인공",
      picture: IMAGES.REVIEWS.PROFILE_2,
      text: `"미국에 있는 아들과는 한 달에 한두 번 통화가 전부였다."`,
      highlight: `"엄마... 제가 왜 이렇게 울고 있는지 모르겠어요."`,
    },
    {
      id: 3,
      name: "김영수 님",
      role: "대기업 임원 퇴임",
      picture: IMAGES.REVIEWS.PROFILE_3,
      text: `"40년 다닌 회사도 나 없이 잘 돌아가고... 내가 뭘 했다고 자랑할 게 있나?"`,
      highlight: `"아버지, 저도 아버지 같은 리더가 되고 싶어요."`,
    },
  ];

  return (
    <section id="reviews" className="reviews-section">
      <div className="reviews-container">
        <div className="section-header">
          <div className="section-meta section-meta--orange">고객 후기</div>
          <h2 className="reviews-title">변화의 순간을 담은 평범한 이야기들</h2>
        </div>

        {isMobile ? (
          // 모바일 레이아웃 - 세로 스크롤
          <div className="reviews-mobile-layout">
            {reviews.map((review) => (
              <div key={review.id} className="review-card-mobile">
                <div className="review-author-mobile">
                  <img
                    src={review.picture}
                    alt={`고객${review.id}`}
                    className="review-avatar-mobile"
                  />
                  <div className="review-author-info-mobile">
                    <div className="review-author-name-mobile">
                      {review.name}
                    </div>
                    <div className="review-author-role-mobile">
                      {review.role}
                    </div>
                  </div>
                </div>
                <div className="review-content-mobile">
                  <div className="review-text-mobile">
                    {review.text}
                    <br />
                    <span className="review-highlight-mobile">
                      {review.highlight}
                    </span>
                  </div>
                  <button
                    className="review-story-btn-mobile"
                    onClick={() => handleStoryClick(review.id)}
                  >
                    감동 사연 보기
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // 데스크톱 레이아웃 - 기존 가로 배치
          <div className="reviews-cards-row">
            {reviews.map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-author">
                  <img
                    src={review.picture}
                    alt={`고객${review.id}`}
                    className="review-avatar"
                  />
                  <div>
                    <div className="review-author-name">{review.name}</div>
                    <div className="review-author-role">{review.role}</div>
                  </div>
                </div>
                <div className="review-text">
                  {review.text}
                  <br />
                  <span className="review-highlight">{review.highlight}</span>
                  <br />
                </div>
                <button
                  className="review-story-btn"
                  onClick={() => handleStoryClick(review.id)}
                >
                  감동 사연 보기
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 모달 - 모바일 최적화 */}
      <div className={`review-story-modal ${isModalOpen ? "active" : ""}`}>
        <div
          className="review-story-modal-overlay"
          onClick={handleCloseModal}
        ></div>
        <div
          className={`review-story-modal-content ${isMobile ? "mobile" : ""} ${
            isSmallMobile ? "small-mobile" : ""
          }`}
        >
          <button
            className="review-story-modal-close"
            onClick={handleCloseModal}
          >
            ×
          </button>
          {!isMobile && (
            <>
              <button
                className="review-story-modal-prev"
                onClick={handlePrevStory}
              >
                ‹
              </button>
              <button
                className="review-story-modal-next"
                onClick={handleNextStory}
              >
                ›
              </button>
            </>
          )}
          <div className="modal-image-container">
            <img
              src={getStoryImage(currentStory)}
              alt={`감동 사연 ${currentStory}`}
              className="modal-story-image"
            />
          </div>
          {isMobile && (
            <div className="mobile-modal-controls">
              <button className="mobile-modal-prev" onClick={handlePrevStory}>
                ‹ 이전
              </button>
              <div className="mobile-modal-indicator">{currentStory} / 3</div>
              <button className="mobile-modal-next" onClick={handleNextStory}>
                다음 ›
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
